import { createSlice } from '@reduxjs/toolkit';
import { loadPersistedFeedFilters, persistFeedFilters, clearPersistedFeedFilters } from './feedFilterPersistence';
import {languages} from "./transformers";

/**
 * The pristine query defaults, with NO persisted filter spread into them.
 *
 * `clearSearchParams` used to reset to `initialState.searchParams`, which is
 * evaluated once at module load WITH the persisted filter already merged in.
 * Reset All therefore cleared sessionStorage and then restored the very filter
 * it had just cleared — reproducibly for a country selection, and invisibly for
 * a session that happened to start unfiltered, which is why it looked
 * intermittent. Reset has to mean the defaults, so the defaults have to exist
 * as their own object.
 */
export const DEFAULT_SEARCH_PARAMS = {
        keywords: { "$or": [], "$and": [], "$not": [], "$english": [], "$hebrew": [], "$arabic": [] },
        sources: [],
        preSources: [],
        keywordOperator: "$and",
        lang: "eng",
        sortBy: "date",
        dateRange: { dateStart: null, dateEnd: null },
        selectedPublishers: 'none',
        selectedCategories: [],
        selectedLanguages: languages,
        excludeLanguages: [],
        excludeCountries: [],
        selectedCountries: [],
        // §2 coverage modes. 'mostly' keeps the 80% share, 'any' qualifies a
        // story on one matching article. Default is the stricter one.
        countryCoverageMode: 'mostly',
        languageCoverageMode: 'mostly',
        selectedBriefLanguage: 'English',

        selectedTimeRange: 'Last 24 Hours',
        publicationsData: [],
        queryUrl: null,
        // Default ON. The feed is read as "what is new", and with this off every
        // long-running story reappeared in Last 24 Hours on the strength of one
        // fresh article. Not in feedFilterPersistence's whitelist, so this
        // default governs every load and every Reset All.
        storiesFromDate: true,
        excludeAlreadyMadeStories: false,
        // Signals are a NARROWING of this feed, not a separate screen, so the
        // flag rides in searchParams with every other filter and the time
        // range, categories, languages and countries all still apply. This
        // replaces the standalone /signals page, which showed a Signal as a
        // bare line of text with no article count, no preview and no way to put
        // it in a bucket.
        signalsOnly: false,
        prs: false,
        verifiedBySourcesToggle: false, // Toggle for minimum sources filter
        minSourcesVerified: 2, // Default minimum number of sources
        sentimentFilter: 'all', // Filter by sentiment: 'all', 'positive', 'negative', 'neutral'
        advancedSort: 'none', // Advanced sort: 'none', 'sentiment', 'ranking', 'shares', 'recent', 'earliest'
        feedSourceFilter: [], // Feed source filter: empty = all, or array of specific sources like ['newsapi_feed', 'newsapi_expansion', 'newsapi_expansion_concepts']
};

const initialState = {
    searchParams: {
        ...DEFAULT_SEARCH_PARAMS,
        // §7 — the applied filter survives a reload. Spread LAST so a stored
        // value wins over the default, and only over the fields
        // feedFilterPersistence whitelists.
        ...(loadPersistedFeedFilters() || {}),
    },
    articles: [],
    story: null,
    selectedStoryIndex: null,
    feedScrollPosition: null,
    claims: [],
    loading: false,
    pristine: true,
    selectedArticles: {},
    selectedStories: {},
    selectedClaims: {},
    selectedArticlesArray: [],
    selectedStoriesArray: [],
    selectedClaimsArray: [],
    lastSelectedClaims: [],
    lastSelectedArticles: [],
    publisherMap: {
        'none': [],
        'israel-palestine': [],
        'ukraine-russia': [],
        'republican-democratic': [],
        'technology': [],
        'science': [],
        'streaming': [],

    },
    urlToBiasMapping: {},
    urlToRankMapping: {},
    dontShowAgainSetBriefLanguage: false,
    userSelectedBriefLanguage: false,
    expandAllTrigger: 0,
    storiesAllExpanded: false,

    // Funnel / routing state (moved from CreateBriefComponent local state)
    activeTab: 'searchStories',   // 'searchStories' | 'searchArticles' | 'brief' | 'fineTuning'
    currentStep: 0,               // 0=Stories, 1=Articles, 2=Claims, 3=Brief
    stories: [],
    storyLocal: null,
    errorStories: null,
    loadingArticles: true,     // Start as true so initial mount shows loader instead of "No stories found"
    hasMoreStories: false,
    isLoadingMoreStories: false,
    totalStories: 0,
    navigateBack: false,
};

const articlesSlice = createSlice({
    name: 'articles',
    initialState,
    reducers: {
        updateSelectedClaimsOrder: (state, { payload }) => {
            state.selectedClaimsArray = payload;
        },
        setPublisherMap: (state, { payload }) => {
            state.publisherMap = payload;
        },
        setStoriesFromDate: (state, { payload }) => {

            state.searchParams.storiesFromDate = payload;
        },
        setExcludeAlreadyMadeStories: (state, { payload }) => {

            state.searchParams.excludeAlreadyMadeStories = payload;
        },
        setSignalsOnly: (state, { payload }) => {
            state.searchParams.signalsOnly = payload;
        },
        setPrs: (state, { payload }) => {

            state.searchParams.prs = payload;
        },
        setSelectedPublishers: (state, { payload }) => {
            state.searchParams.selectedPublishers = payload;
            const pubs = state.publisherMap[payload] || [];
            state.searchParams.publicationsData = pubs;
            state.searchParams.preSources = pubs.map((pub) => ({
                value: pub.sourceUrl,
                label: pub.sourceName,
            }));
        },
        setSelectedCategories: (state, { payload }) => {
            state.searchParams.selectedCategories = payload;
        },
        setSelectedLanguages: (state, { payload }) => {
            state.searchParams.selectedLanguages = payload;
        },
        setExcludeLanguages: (state, { payload }) => {
            state.searchParams.excludeLanguages = payload;
        },
        setExcludeCountries: (state, { payload }) => {
            state.searchParams.excludeCountries = payload;
        },
        setSelectedCountries: (state, { payload }) => {
            state.searchParams.selectedCountries = payload;
        },
        setCountryCoverageMode: (state, { payload }) => {
            state.searchParams.countryCoverageMode = payload === 'any' ? 'any' : 'mostly';
        },
        setLanguageCoverageMode: (state, { payload }) => {
            state.searchParams.languageCoverageMode = payload === 'any' ? 'any' : 'mostly';
        },
        setSelectedBriefLanguage: (state, { payload }) => {
            state.searchParams.selectedBriefLanguage = payload;
        },
        setSelectedTimeRange: (state, { payload }) => {
            state.searchParams.selectedTimeRange = payload;
        },
        setVerifiedBySourcesToggle: (state, { payload }) => {
            state.searchParams.verifiedBySourcesToggle = payload;
        },
        setMinSourcesVerified: (state, { payload }) => {
            state.searchParams.minSourcesVerified = payload;
        },
        setSentimentFilter: (state, { payload }) => {
            state.searchParams.sentimentFilter = payload;
        },
        setAdvancedSort: (state, { payload }) => {
            state.searchParams.advancedSort = payload;
        },
        setFeedSourceFilter: (state, { payload }) => {
            state.searchParams.feedSourceFilter = payload;
        },
        setSearchParam: (state, { payload: [param, newValue, ignore = false] }) => {
            if (!ignore) state.pristine = false;
            state.searchParams[param] = newValue;
        },
        setLoading: (state, { payload }) => {
            state.loading = payload;
        },
        setDontShowAgainSetBriefLanguage: (state, { payload }) => {
            state.dontShowAgainSetBriefLanguage = payload;
        },

        setUserSelectedBriefLanguage: (state, { payload }) => {
            state.userSelectedBriefLanguage = payload;
        },
        setArticles: (state, { payload }) => {
            state.articles = payload;
        },
        setStory: (state, { payload }) => {
            state.story = payload;
        },
        setSelectedStoryIndex: (state, { payload }) => {
            state.selectedStoryIndex = payload;
        },
        setFeedScrollPosition: (state, { payload }) => {
            state.feedScrollPosition = payload;
        },
        clearFeedScrollPosition: (state) => {
            state.feedScrollPosition = null;
        },
        setClaims: (state, { payload }) => {
            state.claims = payload;
        },
        notPristine: (state) => {
            state.pristine = false;
        },
        clearSearchParams: (state) => {
            // The DEFAULTS, not initialState — see DEFAULT_SEARCH_PARAMS above.
            state.searchParams = { ...DEFAULT_SEARCH_PARAMS };
            state.pristine = true;
            state.articles = [];
            state.story = null;
            state.selectedStoryIndex = null;
            state.feedScrollPosition = null;
            state.claims = [];
            state.selectedArticles = {};
            state.selectedClaims = {};
            state.selectedArticlesArray = [];
            state.selectedClaimsArray = [];
            // Reset funnel state
            state.activeTab = 'searchStories';
            state.currentStep = 0;
            state.stories = [];
            state.storyLocal = null;
            state.errorStories = null;
            state.loadingArticles = true; // Show loader since new fetch will fire immediately
            state.navigateBack = false;
            //state.lastSelectedClaims = [];
        },
        clearSelectedArticles: (state) => {
            state.selectedArticles = {};
        },
        clearSelectedStoryIndex: (state) => {
            state.selectedStoryIndex =null;
        },
        clearSelectedArticlesArray: (state) => {
            state.selectedArticles = [];
            state.selectedArticlesArray = [];
        },
        clearSelectedStoriesArray: (state) => {
            state.selectedStories = [];
            state.selectedStoriesArray = [];
        },
        clearSelectedClaims: (state) => {
            state.selectedClaims = {};
        },
        clearSelectedClaimsArray: (state) => {
            state.selectedClaims = {};
            state.selectedClaimsArray = [];
        },
        addArticle: (state, { payload }) => {
            state.articles.push(payload);
        },
        setSelectedArticles: (state, { payload }) => {
            state.selectedArticles = payload;
        },
        setSelectedClaims: (state, { payload }) => {
            state.selectedClaimss = payload;
        },
        setLastSelectedClaims: (state, { payload }) => {
            state.lastSelectedClaims = payload;
        },
        setLastSelectedArticles: (state, { payload }) => {
            state.lastSelectedArticles = payload;
        },
        setSelectedArticlesArray: (state, action) => {
            const { article, isSelected } = action.payload;
            const index = state.selectedArticlesArray.findIndex(item => item.url === article.url);

            if (index !== -1) {
                // Article found, update its selection state
                state.selectedArticlesArray[index] = { ...state.selectedArticlesArray[index], isSelected };
            } else {
                // New article, add it to the array with isSelected and bias
                state.selectedArticlesArray.push({ ...article, isSelected });
            }
        },
        setSelectedStoriesArray: (state, action) => {
            const { story, isSelected } = action.payload;
            const index = state.selectedStoriesArray.findIndex(item => item._id === story?.groupSubject._id);

            if (index !== -1) {
                // Article found, update its selection state
                state.selectedStoriesArray[index] = { ...state.selectedStoriesArray[index], isSelected };
            } else {
                // New article, add it to the array with isSelected and bias
                state.selectedStoriesArray.push({ ...story, isSelected });
            }
        },
        setSelectedClaimsArray: (state, action) => {
            const { claim, isSelected } = action.payload;
            const index = state.selectedClaimsArray.findIndex(item => item._id === claim._id);

            if (index !== -1) {
                // Article found, update its selection state
                state.selectedClaimsArray[index] = { ...state.selectedClaimsArray[index], isSelected };
            } else {
                // New article, add it to the array with isSelected and bias
                state.selectedClaimsArray.push({ ...claim, isSelected });
            }
        },
        setUrlToBiasMapping: (state, action) => {
            state.urlToBiasMapping = action.payload;
        },
        setUrlToRankMapping: (state, action) => {
            state.urlToRankMapping = action.payload;
        },
        triggerExpandAll: (state) => {
            state.expandAllTrigger = state.expandAllTrigger + 1;
        },
        setStoriesAllExpanded: (state, { payload }) => {
            state.storiesAllExpanded = payload;
        },

        // Funnel / routing reducers
        setActiveTab: (state, { payload }) => {
            state.activeTab = payload;
        },
        setCurrentStep: (state, { payload }) => {
            state.currentStep = payload;
        },
        setStories: (state, { payload }) => {
            state.stories = payload;
        },
        appendStories: (state, { payload }) => {
            state.stories = [...state.stories, ...payload];
        },
        setStoryLocal: (state, { payload }) => {
            state.storyLocal = payload;
        },
        setErrorStories: (state, { payload }) => {
            state.errorStories = payload;
        },
        setLoadingArticles: (state, { payload }) => {
            state.loadingArticles = payload;
        },
        setHasMoreStories: (state, { payload }) => {
            state.hasMoreStories = payload;
        },
        setIsLoadingMoreStories: (state, { payload }) => {
            state.isLoadingMoreStories = payload;
        },
        setTotalStories: (state, { payload }) => {
            state.totalStories = payload;
        },
        setNavigateBack: (state, { payload }) => {
            state.navigateBack = payload;
        },
    },
});

// Action creators are generated for each case reducer function
export const {
    setUrlToBiasMapping,
    setUrlToRankMapping,
    updateSelectedClaimsOrder,
    notPristine,
    setPublisherMap,
    setSearchParam,
    setArticles,
    setStory,
    setSelectedStoryIndex,
    setFeedScrollPosition,
    clearFeedScrollPosition,
    setClaims,
    setLoading,
    setDontShowAgainSetBriefLanguage,
    setUserSelectedBriefLanguage,
    clearSearchParams,
    setSelectedArticles,
    setSelectedClaims,
    setLastSelectedClaims,
    setLastSelectedArticles,
    setSelectedArticlesArray,
    setSelectedStoriesArray,
    setSelectedClaimsArray,
    setSelectedPublishers,
    setSelectedCategories,
    setSelectedLanguages,
    setExcludeLanguages,
    setExcludeCountries,
    setSelectedCountries,
    setCountryCoverageMode,
    setLanguageCoverageMode,
    setSelectedBriefLanguage,
    setSelectedTimeRange,
    addArticle,
    clearSelectedArticles,
    clearSelectedStoryIndex,
    clearSelectedArticlesArray,
    clearSelectedStoriesArray,
    clearSelectedClaims,
    clearSelectedClaimsArray,
    setStoriesFromDate,
    setExcludeAlreadyMadeStories,
    setSignalsOnly,
    setPrs,
    setVerifiedBySourcesToggle,
    setMinSourcesVerified,
    setSentimentFilter,
    setAdvancedSort,
    setFeedSourceFilter,
    triggerExpandAll,
    setStoriesAllExpanded,
    setActiveTab,
    setCurrentStep,
    setStories,
    appendStories,
    setStoryLocal,
    setErrorStories,
    setLoadingArticles,
    setHasMoreStories,
    setIsLoadingMoreStories,
    setTotalStories,
    setNavigateBack,
} = articlesSlice.actions;

export default articlesSlice.reducer;
export const articlesReducer = articlesSlice.reducer;